import React from "react";
import PropTypes from "prop-types";
import EditorialMemberCard from "../EditorialMemberCard";
import FriendListRow from "../FriendListRow";

const mapListVariant = (variant = "primary") => {
  if (variant === "default") {
    return "primary";
  }
  return variant;
};

const resolveEditorialRoleAndLocation = ({
  userInfoPrimaryText,
  userInfoSecondaryText,
}) => {
  const primary = typeof userInfoPrimaryText === "string" ? userInfoPrimaryText.trim() : "";
  const secondary = typeof userInfoSecondaryText === "string" ? userInfoSecondaryText.trim() : "";

  return { roleText: primary, locationText: secondary };
};

export default function FriendshipUserCard({
  layout = "grid",
  userId,
  username,
  displayName,
  avatarUrl,
  // eslint-disable-next-line no-unused-vars
  compactHeader = true,
  identityMetaLines = [],
  relationshipLabel,
  userInfoPrimaryText,
  userInfoPrimaryContent,
  userInfoSecondaryText,
  metaItems = [],
  summary,
  snapshotLines = [],
  snapshotFallback,
  connectionHeading,
  connectionValue,
  previewAriaLabel,
  avatarAlt,
  onPreview,
  presenceStatus = null,
  actions = [],
  publicMedia = false,
  muted = false,
  badgeText = null,
}) {
  const normalizedActions = actions.filter(Boolean);
  const resolvedDisplayName = displayName || username;

  if (layout === "list") {
    return (
      <FriendListRow
        userId={userId}
        username={username}
        displayName={resolvedDisplayName}
        identityMetaLines={identityMetaLines}
        relationshipLabel={relationshipLabel}
        metaItems={metaItems}
        summary={summary}
        snapshotLines={snapshotLines}
        snapshotFallback={snapshotFallback}
        connectionHeading={connectionHeading}
        connectionValue={connectionValue}
        previewAriaLabel={previewAriaLabel}
        avatarAlt={avatarAlt}
        avatarUrl={avatarUrl}
        publicMedia={publicMedia}
        onPreview={onPreview}
        presenceStatus={presenceStatus}
        actions={normalizedActions.map((action) => ({
          ...action,
          variant: mapListVariant(action.variant),
        }))}
      />
    );
  }

  const { roleText, locationText } = resolveEditorialRoleAndLocation({
    userInfoPrimaryText,
    userInfoSecondaryText,
  });

  return (
    <EditorialMemberCard
      userId={userId}
      username={username}
      displayName={resolvedDisplayName}
      roleText={roleText}
      roleContent={userInfoPrimaryContent ?? roleText}
      locationText={locationText}
      actions={normalizedActions.map((action) => ({
        ...action,
        ariaLabel:
          action.ariaLabel || (action.label && resolvedDisplayName ? `${action.label} ${resolvedDisplayName}` : action.label),
      }))}
      onCardClick={onPreview}
      cardAriaLabel={previewAriaLabel}
      avatarAlt={avatarAlt}
      presenceStatus={presenceStatus}
      avatarUrl={avatarUrl}
      publicMedia={publicMedia}
      muted={muted}
      badgeText={badgeText}
    />
  );
}

const actionPropType = PropTypes.shape({
  key: PropTypes.string,
  label: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  busyLabel: PropTypes.string,
  variant: PropTypes.oneOf(["primary", "default", "secondary", "success", "danger"]),
  disabled: PropTypes.bool,
  busy: PropTypes.bool,
  onClick: PropTypes.func,
});

FriendshipUserCard.propTypes = {
  layout: PropTypes.oneOf(["grid", "list"]),
  userId: PropTypes.string.isRequired,
  username: PropTypes.string,
  displayName: PropTypes.string,
  avatarUrl: PropTypes.string,
  compactHeader: PropTypes.bool,
  identityMetaLines: PropTypes.arrayOf(PropTypes.string),
  relationshipLabel: PropTypes.string,
  userInfoPrimaryText: PropTypes.string,
  userInfoPrimaryContent: PropTypes.node,
  userInfoSecondaryText: PropTypes.string,
  metaItems: PropTypes.arrayOf(PropTypes.string),
  summary: PropTypes.string,
  snapshotLines: PropTypes.arrayOf(PropTypes.string),
  snapshotFallback: PropTypes.string,
  connectionHeading: PropTypes.string,
  connectionValue: PropTypes.string,
  previewAriaLabel: PropTypes.string,
  avatarAlt: PropTypes.string,
  onPreview: PropTypes.func,
  presenceStatus: PropTypes.shape({
    online: PropTypes.bool,
    lastSeen: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    placement: PropTypes.oneOf(["br", "bl", "tr", "tl"]),
    size: PropTypes.string,
    offset: PropTypes.string,
    border: PropTypes.string,
  }),
  actions: PropTypes.arrayOf(actionPropType),
  publicMedia: PropTypes.bool,
  muted: PropTypes.bool,
  badgeText: PropTypes.node,
};
